import { Route, Routes, useNavigate } from 'react-router-dom'
import { IPublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import React, { Suspense } from 'react'
import { CustomNavigationClient } from 'utils/NavigationClient'

import { Layout } from './layout/Layout'
import { ThemeProvider } from '@vale/agata-components-react'
import { useTheme } from './contexts/ThemeProvider'
import { setupResponseInterceptor } from './services/connect'
import { Loading } from 'components/Loading'

type RouterProps = {
  msalInstance: IPublicClientApplication
}
const Home = React.lazy(() =>
  import('./pages/Home').then(module => ({ default: module.Home }))
)

const About = React.lazy(() =>
  import('./pages/About').then(module => ({ default: module.About }))
)
const Workspace = React.lazy(() =>
  import('./pages/Workspace/Workspace').then(module => ({
    default: module.Workspace
  }))
)
const AreaDeNegocios = React.lazy(() =>
  import('./pages/AreaDeNegocios').then(module => ({
    default: module.AreaDeNegocios
  }))
)
const AreaDeNegocio = React.lazy(() =>
  import('./pages/AreaDeNegocio').then(module => ({
    default: module.AreaDeNegocio
  }))
)
const AreasDeTrabalho = React.lazy(() =>
  import('./pages/AreasDeTrabalho').then(module => ({
    default: module.AreasDeTrabalho
  }))
)
const Plataforma = React.lazy(() =>
  import('./pages/Plataforma').then(module => ({ default: module.Plataforma }))
)
const Equipe = React.lazy(() =>
  import('./pages/Equipe').then(module => ({ default: module.Equipe }))
)
const Microfrontend = React.lazy(() =>
  import('pages/Microfrontend').then(module => ({
    default: module.Microfrontend
  }))
)

const CatalogoDeItens = React.lazy(() =>
  import('pages/CatalogoDeItens').then(module => ({
    default: module.CatalogoDeItens
  }))
)

const MeuEspaco = React.lazy(() =>
  import('pages/MeuEspaco').then(module => ({ default: module.MeuEspaco }))
)

const PermissaoNegada = React.lazy(() =>
  import('./pages/PermissaoNegada').then(module => ({
    default: module.PermissaoNegada
  }))
)

const AtualizarItem = React.lazy(() =>
  import('pages/CriarItem').then(module => ({ default: module.AtualizarItem }))
)

const CriarItem = React.lazy(() =>
  import('pages/CriarItem').then(module => ({ default: module.CriarItem }))
)

const DashboardFullScreen = React.lazy(() =>
  import('pages/DashboardFullScreen').then(module => ({
    default: module.DashboardFullScreen
  }))
)

const PowerBiFullScreen = React.lazy(() =>
  import('pages/PowerBiFullScreen').then(module => ({
    default: module.PowerBiFullScreen
  }))
)

export function Router({ msalInstance }: RouterProps) {
  const navigate = useNavigate()
  const navigationClient = new CustomNavigationClient(navigate)
  msalInstance.setNavigationClient(navigationClient)
  setupResponseInterceptor(navigate)

  const rotasCatalogoDeItens = [
    'catalogo-itens',
    'workspaces/:id/catalogo-itens',
    'plataformas/:codPlataforma/catalogo-itens',
    'plataformas/:codPlataforma/equipes/:codEquipe/workspaces/:id/catalogo-itens'
  ]

  const rotasAtualizarItem = [
    'catalogo-itens/editar-item/:idComponente',
    'plataformas/:codPlataforma/catalogo-itens/editar-item/:idComponente'
  ]

  const rotasCriarItem = [
    'catalogo-itens/criar-item',
    'plataformas/:codPlataforma/catalogo-itens/criar-item'
  ]

  const rotasDashboardFullScreen = [
    '/dashboard/:id',
    'plataformas/:codPlataforma/dashboard/:id'
  ]

  const rotasPowerBiFullScreen = [
    '/powerBi/:id',
    'plataformas/:codPlataforma/powerBi/:id'
  ]

  const rotasMicrofrontend = [
    '/app/:mf',
    'plataformas/app/:mf',
    'plataformas/:codPlataforma/app/:mf'
  ]

  const rotasWorkspaceSemMF = [
    'workspaces',
    'workspaces/:id',
    'plataformas/:codPlataforma/workspaces',
    'plataformas/:codPlataforma/workspaces/:id',
    'plataformas/:codPlataforma/equipes/:codEquipe/workspaces/:id'
  ]

  const { themeName } = useTheme()

  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider isDarkMode={themeName === 'dark'}>
        <Routes>
          <Route path="/:mf" element={<Microfrontend />} />

          <Route path="/" element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="home" element={<Home />} />
            <Route path="about" element={<About />} />

            {rotasCatalogoDeItens.map(rota => (
              <Route
                key={rota}
                path={rota}
                element={
                  <Suspense fallback={<Loading />}>
                    <CatalogoDeItens />
                  </Suspense>
                }
              />
            ))}

            {rotasCriarItem.map(rota => (
              <Route
                key={rota}
                path={rota}
                element={
                  <Suspense fallback={<Loading />}>
                    <CriarItem />
                  </Suspense>
                }
              />
            ))}

            {rotasAtualizarItem.map(rota => (
              <Route
                key={rota}
                path={rota}
                element={
                  <Suspense fallback={<Loading />}>
                    <AtualizarItem />
                  </Suspense>
                }
              />
            ))}

            {rotasMicrofrontend.map(rota => (
              <Route
                key={rota}
                path={rota}
                element={
                  <Suspense fallback={<Loading />}>
                    <Microfrontend />
                  </Suspense>
                }
              />
            ))}

            {rotasDashboardFullScreen.map(rota => (
              <Route
                key={rota}
                path={rota}
                element={
                  <Suspense fallback={<Loading />}>
                    <DashboardFullScreen />
                  </Suspense>
                }
              />
            ))}

            {rotasPowerBiFullScreen.map(rota => (
              <Route
                key={rota}
                path={rota}
                element={
                  <Suspense fallback={<Loading />}>
                    <PowerBiFullScreen />
                  </Suspense>
                }
              />
            ))}

            {rotasWorkspaceSemMF.map(rota => (
              <Route
                key={rota}
                path={rota}
                element={
                  <Suspense fallback={<Loading />}>
                    <Workspace isMF={false} />
                  </Suspense>
                }
              />
            ))}

            <Route
              path="plataformas/:codPlataforma/area-negocios/:codAreaNegocio/equipes/:codEquipe/workspaces/:id"
              element={
                <Suspense fallback={<Loading />}>
                  <Workspace isMF={true} />
                </Suspense>
              }
            />
            <Route
              path="plataformas/:codPlataforma"
              element={
                <Suspense fallback={<Loading />}>
                  <Plataforma />
                </Suspense>
              }
            />
            <Route
              path="plataformas/:codPlataforma/area-negocios"
              element={
                <Suspense fallback={<Loading />}>
                  <AreaDeNegocios />
                </Suspense>
              }
            />
            <Route
              path="plataformas/:codPlataforma/area-negocios/:codAreaNegocio"
              element={
                <Suspense fallback={<Loading />}>
                  <AreaDeNegocio />
                </Suspense>
              }
            />
            <Route
              path="plataformas/:codPlataforma/area-negocios/:codAreaNegocio/equipes/:idEquipe"
              element={
                <Suspense fallback={<Loading />}>
                  <Equipe />
                </Suspense>
              }
            />
            <Route
              path="plataformas/:codPlataforma/equipes/:codEquipe/workspaces"
              element={
                <Suspense fallback={<Loading />}>
                  <AreasDeTrabalho />
                </Suspense>
              }
            />
            <Route
              path="meu-espaco"
              element={
                <Suspense fallback={<Loading />}>
                  <MeuEspaco />
                </Suspense>
              }
            />
            <Route
              path="permissao-negada"
              element={
                <Suspense fallback={<Loading />}>
                  <PermissaoNegada />
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </ThemeProvider>
    </MsalProvider>
  )
}
